import { ActionIconButton } from '@/shared/components/ui/ActionIconButton';
import { Button } from '@/shared/components/ui/Button';
import { useClientPagination } from '@/shared/hooks/useClientPagination';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { confirm } from '@/shared/lib/confirm';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import {
  type DepartmentRow,
  createDepartment,
  deleteDepartment,
  listDepartments,
  updateDepartment,
} from '../api';

export function DepartmentsTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['departments'], queryFn: listDepartments });
  const [editing, setEditing] = useState<DepartmentRow | 'new' | null>(null);
  const all = q.data ?? [];
  const { visible, hasMore, sentinelRef } = useClientPagination(all);

  const delMut = useMutation({
    mutationFn: deleteDepartment,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['departments'] }),
  });

  return (
    <section className="flex h-full flex-col overflow-hidden">
      <header className="mb-5 flex flex-shrink-0 items-center justify-between">
        <div>
          <h2
            className="font-bold text-[20px] text-n-900"
            style={{ letterSpacing: '-.02em', marginBottom: '4px' }}
          >
            Departamentos
          </h2>
          <p className="text-[13px] text-n-500">Organize sua equipe por área.</p>
        </div>
        <Button onClick={() => setEditing('new')}>
          <Plus aria-hidden="true" />
          Novo departamento
        </Button>
      </header>

      {!q.isLoading && all.length > 0 && (
        <p className="mb-2 flex-shrink-0 text-[12px] text-n-500">
          {visible.length === all.length
            ? `${all.length} ${all.length === 1 ? 'departamento' : 'departamentos'}`
            : `Mostrando ${visible.length} de ${all.length} departamentos`}
        </p>
      )}
      <div className="min-h-0 flex flex-col overflow-y-auto rounded-md border border-n-200 bg-n-0 shadow-sm">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 z-10 border-b border-n-200 bg-n-50 text-left">
            <tr>
              <th className="px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Nome
              </th>
              <th className="w-[140px] px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Cor
              </th>
              <th className="w-[180px] px-[14px] py-[10px] text-right text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr>
                <td colSpan={3} className="px-3 py-8 text-center">
                  <Loader2 size={18} className="mx-auto animate-spin text-n-400" />
                </td>
              </tr>
            )}
            {!q.isLoading && all.length === 0 && (
              <tr>
                <td colSpan={3} className="px-3 py-12 text-center text-n-500">
                  Nenhum departamento cadastrado.
                </td>
              </tr>
            )}
            {visible.map((d) => (
              <tr key={d.id} className="border-b border-n-100 last:border-b-0 hover:bg-n-50">
                <td className="px-[14px] py-[11px] font-medium text-n-900">{d.name}</td>
                <td className="px-[14px] py-[11px]">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block h-4 w-4 rounded"
                      style={{ background: d.color }}
                    />
                    <span className="font-mono text-[11px]">{d.color}</span>
                  </div>
                </td>
                <td className="px-[14px] py-[11px]">
                  <div className="flex justify-end gap-1">
                    <ActionIconButton
                      icon={Pencil}
                      label="Editar"
                      tone="brand"
                      onClick={() => setEditing(d)}
                    />
                    <ActionIconButton
                      icon={Trash2}
                      label="Apagar"
                      tone="danger"
                      onClick={async () => {
                        const ok = await confirm({
                          title: `Apagar "${d.name}"?`,
                          confirmText: 'Apagar',
                          variant: 'danger',
                        });
                        if (ok) delMut.mutate(d.id);
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
            {hasMore && (
              <tr ref={sentinelRef}>
                <td colSpan={3} className="py-4 text-center text-[12px] text-n-500">
                  <span className="inline-flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Carregando mais…
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <DepartmentFormModal
          dep={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function DepartmentFormModal({
  dep,
  onClose,
}: {
  dep: DepartmentRow | null;
  onClose: () => void;
}) {
  useEscapeClose(true, onClose);
  const qc = useQueryClient();
  const [name, setName] = useState(dep?.name ?? '');
  const [color, setColor] = useState(dep?.color ?? '#0A4EE3');
  const mut = useMutation({
    mutationFn: async () => {
      if (dep) await updateDepartment(dep.id, { name, color });
      else await createDepartment({ name, color });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['departments'] });
      onClose();
    },
  });
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-[380px] rounded-lg bg-n-0 p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-[16px]">
            {dep ? 'Editar departamento' : 'Novo departamento'}
          </h3>
          <button type="button" onClick={onClose} className="text-n-500" aria-label="Fechar">
            <X size={16} />
          </button>
        </div>
        <div className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Nome</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-md border border-n-200 px-3 py-2 text-[13px]"
              autoFocus
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Cor</span>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-10 w-20 rounded-md border border-n-200"
            />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !name.trim()}>
            {mut.isPending && <Loader2 size={12} className="animate-spin" />}
            Salvar
          </Button>
        </div>
      </div>
    </div>
  );
}
